/** @typedef {import("./types").CalendarDate} CalendarDate */
import { isBefore, isSameDay, parseLocalDateValue } from "src/services/helpers";
import { suppressEscapeFor } from "src/services/escape-key-guard";

const daysOfWeek = [
  { short: "Su", full: "Sunday" },
  { short: "Mo", full: "Monday" },
  { short: "Tu", full: "Tuesday" },
  { short: "We", full: "Wednesday" },
  { short: "Th", full: "Thursday" },
  { short: "Fr", full: "Friday" },
  { short: "Sa", full: "Saturday" }
];
const calendarSize = 42;

/**
 * @param {number} year
 * @returns boolean
 */
function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Returns the total days in month, accounting for leap year
 *
 * @param {number} month
 * @param {number} year
 * @returns number
 */
function getDaysInMonth(month, year) {
  switch (month) {
    case 1: // February
      return isLeapYear(year) ? 29 : 28;
    case 3:
    case 5:
    case 8:
    case 10: // April, June, September, November
      return 30;
    default:
      return 31;
  }
}

/**
 * Custom calendar date picker implementation.
 *
 * @element mx-reservation-calendar
 * @fires CustomEvent<number>
 * @example
 * <mx-reservation-calendar
 *   onDateChanged=${handleDateSelect}
 *   value=${value}>
 * </mx-reservation-calendar>
 */
class ReservationCalendar extends HTMLElement {
  static observedAttributes = ["value"];
  constructor() {
    super();
    const today = new Date();
    this.month = today.getMonth();
    this.year = today.getFullYear();
    this.value = null;
  }

  connectedCallback() {
    this.render();
    this.addEventListener("click", this);
    this.addEventListener("keydown", this);
    this.focusedDateIndex = -1;

    // Store potential trigger element when calendar opens
    this._triggerElement = /** @type {HTMLElement} */ (document.activeElement);
  }

  disconnectedCallback() {
    // Cleanup is handled by the component removal
  }

  closeCalendar() {
    suppressEscapeFor();
    this.style.display = "none";
    this.setAttribute("aria-hidden", "true");
    this.announce("Date picker closed");

    if (this._triggerElement instanceof HTMLElement) {
      this._triggerElement.focus();
    }

    this.dispatchEvent(new CustomEvent("CalendarClose", { bubbles: true }));
  }

  /**
   * @param {string} name
   * @param  {string} _
   * @param {string} newValue
   */
  attributeChangedCallback(name, _, newValue) {
    if (name === "value") {
      const date = parseLocalDateValue(newValue);
      if (!date) return;

      this.year = date.getFullYear();
      this.month = date.getMonth();
      this.value = date;
      this.render();
    }
  }

  /**
   * @param {Event} event
   * @property {number} detail
   * @fires DateChanged
   */
  handleEvent(event) {
    if (event.type === "keydown") {
      this.handleKeyDown(/** @type {KeyboardEvent} */(event));
      return;
    }

    const target = /** @type {HTMLDivElement} */ (event.target);
    event.preventDefault();
    event.stopPropagation();

    if (target.classList.contains("mx-calendar__btn")) {
      // Skip if button is disabled
      if (target.hasAttribute('disabled') || target.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }
      
      event.preventDefault();
      const prevType = target.getAttribute("data-type");
      const prevValue = target.getAttribute("data-value");

      const type = prevType;
      const direction = Number(prevValue);

      // Store last navigation direction for Enter continuation
      this._lastNavDirection = direction;

      if (type === "year") {
        this.year = new Date(this.year + direction, this.month).getFullYear();
      } else if (type === "month") {
        let nextMonth = this.month + direction;
        if (nextMonth > 11) {
          nextMonth = 0;
          this.year++;
        } else if (nextMonth < 0) {
          nextMonth = 11;
          this.year--;
        }

        this.month = nextMonth;
      }

      // Announce
      const newMonthYear = new Date(this.year, this.month).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      this.announce(`Calendar now showing ${newMonthYear}`);

      this.render();

      // Restore focus to the same navigation button after rerender.
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.focusNavigationButton(type, direction);
        }, 10);
      });

      return;
    }

    if (
      target.hasAttribute("data-value") &&
      target.getAttribute("data-disabled") === "false"
    ) {
      // @ts-ignore
      this.selectDate(target);
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    const target = /** @type {HTMLDivElement} */ (event.target);

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.closeCalendar();
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();

      if (event.shiftKey) {
        this.handleShiftTab();
      } else {
        this.handleTab();
      }

      return;
    }

    // Handle navigation button keyboard events
    if (target.classList.contains("mx-calendar__btn")) {
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "Spacebar" ||
        event.code === "Space"
      ) {
        event.preventDefault();
        target.dispatchEvent(
          new MouseEvent("click", { bubbles: true })
        );

        return;
      }
    }

    // Handle date grid keyboard navigation
    if (target.classList.contains("mx-calendar__date") && target.getAttribute("data-disabled") === "false") {
      const dates = this.getCalendarDates();
      const currentIndex = dates.findIndex(d => d.value.getTime() === Number(target.getAttribute("data-value")));

      if (currentIndex === -1) return;

      let newIndex = currentIndex;

      switch (event.key) {
        case "ArrowRight":
          event.preventDefault();
          newIndex = currentIndex + 1;
          break;
        case "ArrowLeft":
          event.preventDefault();
          newIndex = currentIndex - 1;
          break;
        case "ArrowDown":
          event.preventDefault();
          newIndex = currentIndex + 7;
          break;
        case "ArrowUp":
          event.preventDefault();
          newIndex = currentIndex - 7;
          break;
        case "Home":
          event.preventDefault();
          // Move to first day of current week (Sunday)
          const currentWeekStart = currentIndex - (currentIndex % 7);
          newIndex = currentWeekStart;
          break;
        case "End":
          event.preventDefault();
          // Move to last day of current week (Saturday)
          const currentWeekEnd = currentIndex - (currentIndex % 7) + 6;
          newIndex = Math.min(currentWeekEnd, dates.length - 1);
          break;
        case "PageUp":
          event.preventDefault();
          if (event.shiftKey) {
            // Previous year
            this.year--;
          } else {
            // Previous month
            this.month--;
            if (this.month < 0) {
              this.month = 11;
              this.year--;
            }
          }
          this.render();
          // Focus on same day of month or last day if not exist
          this.focusSameDayOfMonth(currentIndex);
          return;
        case "PageDown":
          event.preventDefault();
          if (event.shiftKey) {
            // Next year
            this.year++;
          } else {
            // Next month
            this.month++;
            if (this.month > 11) {
              this.month = 0;
              this.year++;
            }
          }
          this.render();
          // Focus on same day of month or last day if not exist
          this.focusSameDayOfMonth(currentIndex);
          return;
        case "Enter":
          event.preventDefault();
          // @ts-ignore
          this.selectDate(target);
          return;
        case " ":
        case "Spacebar":
          event.preventDefault();
          // @ts-ignore
          this.selectDate(target, true);
          return;
        default:
          return;
      }

      // Ensure new index is within bounds
      if (newIndex >= 0 && newIndex < dates.length) {
        const newDate = dates[newIndex];
        if (newDate.isDisabled) {
          return; // Don't move to disabled dates
        }

        // Focus the new date
        const newTarget = /** @type {HTMLDivElement} */ (this.querySelector(`[data-value="${newDate.value.getTime()}"]`));
        if (newTarget) {
          this.setActiveDateFocus(newDate.value.getTime())
          newTarget.focus();
          this.announceDate(newDate);
        }
      }
    }
  }

  getCalendarDates() {
    const dates = [];
    const today = new Date();
    const firstDay = new Date(this.year, this.month).getDay();
    const totalDaysInMonth = getDaysInMonth(this.month, this.year);
    const totalDaysInPrevMonth = getDaysInMonth(this.month - 1, this.year);
    const previousMonthStartDate = totalDaysInPrevMonth - (firstDay - 1);

    // Previous month's dates
    for (let i = previousMonthStartDate; i <= totalDaysInPrevMonth; i++) {
      const date = new Date(this.year, this.month - 1, i);
      dates.push({
        date: i,
        value: date,
        isCurrentMonth: false,
        isDisabled: today > date,
        isToday: false,
      });
    }

    // Current month's dates
    for (let i = 1; i <= totalDaysInMonth; i++) {
      const date = new Date(this.year, this.month, i);
      const isToday = isSameDay(today, date);
      const isDisabled = isBefore(date, today) && !isToday;

      dates.push({
        date: i,
        value: date,
        isCurrentMonth: true,
        isDisabled,
        isToday,
      });
    }

    // Next month's dates
    if (dates.length < calendarSize) {
      const offset = calendarSize - dates.length;
      for (let i = 1; i <= offset; i++) {
        const date = new Date(this.year, this.month + 1, i);
        dates.push({
          date: i,
          value: date,
          isCurrentMonth: false,
          isDisabled: false,
          isToday: false,
        });
      }
    }

    return dates;
  }

  setInitialFocus() {
    const dates = this.getCalendarDates();
    const selectedDate = parseLocalDateValue(this.value);

    // Focus priority: selected date → today → first available date
    const selectedDateIndex = dates.findIndex(
      (d) => selectedDate && isSameDay(d.value, selectedDate)
    );

    const todayIndex = dates.findIndex(d => d.isToday && !d.isDisabled);
    const firstAvailableIndex = dates.findIndex(d => !d.isDisabled);

    const focusIndex = selectedDateIndex >= 0 ? selectedDateIndex :
      (todayIndex >= 0 ? todayIndex : firstAvailableIndex);

    if (focusIndex >= 0) {
      const targetDate = dates[focusIndex];
      const targetValue = targetDate.value.getTime();

      this.setActiveDateFocus(targetValue);

      const target = this.querySelector(`[data-value="${targetValue}"]`);
      if (target instanceof HTMLElement) {
        this.applyFocusStyle(target);
        target.focus();
        this.announceDate(targetDate);
      }
    }
  }

  /**
   * @param {CalendarDate} date
   */
  announceDate(date) {
    const announcement = this.querySelector("#calendar-announcement");
    if (!announcement) return;

    // Include day of week with full date
    const dateString = date.value.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    let message = dateString;

    // Announce if it's today
    if (date.isToday) {
      message += ", This is today";
    }

    // Announce if date is unavailable
    if (date.isDisabled) {
      message += ", Not available";
    }

    // Announce if from different month
    if (!date.isCurrentMonth) {
      message += ", Different month";
    }

    // Announce if this date is selected
    const selectedDate = parseLocalDateValue(this.value);
    if (selectedDate && isSameDay(date.value, selectedDate)) {
      message += ", Selected";
    }

    announcement.textContent = message;
  }

  /**
   * @param {string} message
   */
  announce(message) {
    const announcement = this.querySelector("#calendar-announcement");
    if (announcement) {
      announcement.textContent = message;
    }
  }

  clearFocusStyles() {
    // No longer needed - CSS handles focus styles
  }

  /**
   * @param {HTMLElement} element
   */
  applyFocusStyle(element) {
    // No longer needed - CSS handles focus styles
    element.focus();
  }

  focusFirstAvailableDate() {
    const dates = this.getCalendarDates();
    // Find first available date in current month
    const firstAvailableIndex = dates.findIndex(d => d.isCurrentMonth && !d.isDisabled);

    if (firstAvailableIndex >= 0) {
      const targetDate = dates[firstAvailableIndex];
      const targetValue = targetDate.value.getTime();

      this.setActiveDateFocus(targetValue);

      const target = this.querySelector(`[data-value="${targetValue}"]`);
      if (target instanceof HTMLElement) {
        this.applyFocusStyle(target);
        target.focus();
        this.announceDate(targetDate);
      }
    }
  }

  /**
   * @param {string} type
   * @param {number} direction
   */
  focusNavigationButton(type, direction) {
    const selector = `.mx-calendar__btn[data-type="${type}"][data-value="${direction}"]`;
    const target = this.querySelector(selector);

    if (target instanceof HTMLElement) {
      this.applyFocusStyle(target);
      target.focus();
    }
  }

  handleTab() {
    const focusableElements = this.getFocusableElements();
    const currentElement = document.activeElement;

    if (!(currentElement instanceof HTMLElement) || !this.contains(currentElement)) {
      // If focus is outside calendar, focus first element
      const firstElement = focusableElements[0];
      if (firstElement instanceof HTMLElement) {
        // Force focus by setting tabindex and calling focus()
        firstElement.setAttribute('tabindex', '0');
        firstElement.focus();
      }
      return;
    }

    const currentIndex = focusableElements.indexOf(currentElement);
    
    if (currentIndex === -1) {
      // If focus is on a date not in tab sequence, move to first focusable element
      const firstElement = focusableElements[0];
      if (firstElement instanceof HTMLElement) {
        // Force focus by setting tabindex and calling focus()
        firstElement.setAttribute('tabindex', '0');
        firstElement.focus();
      }
    } else {
      // Move to next element, wrap to first if at last
      const nextIndex = (currentIndex + 1) % focusableElements.length;
      const nextElement = focusableElements[nextIndex];
      if (nextElement instanceof HTMLElement) {
        // Force focus by setting tabindex and calling focus()
        nextElement.setAttribute('tabindex', '0');
        nextElement.focus();
      }
    }
  }

  handleShiftTab() {
    const focusableElements = this.getFocusableElements();
    const currentElement = document.activeElement;

    if (!(currentElement instanceof HTMLElement) || !this.contains(currentElement)) {
      // If focus is outside calendar, focus last element
      const lastElement = focusableElements[focusableElements.length - 1];
      if (lastElement instanceof HTMLElement) {
        this.applyFocusStyle(lastElement);
        lastElement.focus();
      }
      return;
    }

    const currentIndex = focusableElements.indexOf(currentElement);
    
    if (currentIndex === -1) {
      // If focus is on a date not in tab sequence, move to last focusable element
      const lastElement = focusableElements[focusableElements.length - 1];
      if (lastElement instanceof HTMLElement) {
        this.applyFocusStyle(lastElement);
        lastElement.focus();
      }
    } else {
      // Move to previous element, wrap to last if at first
      const prevIndex = currentIndex - 1;
      const targetIndex = prevIndex < 0 ? focusableElements.length - 1 : prevIndex;
      const prevElement = focusableElements[targetIndex];
      if (prevElement instanceof HTMLElement) {
        this.applyFocusStyle(prevElement);
        prevElement.focus();
      }
    }
  }

  getFocusableElements() {
    // Get navigation buttons in correct visual order
    const prevYear = this.querySelector('[data-type="year"][data-value="-1"]');
    const prevMonth = this.querySelector('[data-type="month"][data-value="-1"]');
    const nextMonth = this.querySelector('[data-type="month"][data-value="1"]');
    const nextYear = this.querySelector('[data-type="year"][data-value="1"]');
    let focusableDate = this.querySelector('.mx-calendar__date[tabindex="0"]');
    
    // If no date has tabindex="0", find the first focusable date
    if (!focusableDate) {
      focusableDate = this.querySelector('.mx-calendar__date[data-disabled="false"]');
    }

    // Return navigation buttons plus the active date in the grid.
    // Filter out null elements and disabled buttons
    const elements = [
      prevYear,
      prevMonth,
      nextMonth,
      nextYear,
      focusableDate
    ].filter(el => el && !(el instanceof HTMLElement && el.hasAttribute('disabled')));
    
    return elements;
  }

  focusSameDayOfMonth(oldIndex) {
    const dates = this.getCalendarDates();
    const oldDate = dates[oldIndex];
    if (!oldDate) return;

    const targetDay = oldDate.value.getDate();
    const newDates = this.getCalendarDates();

    // Find same day of month, or last day if not exist
    let targetIndex = newDates.findIndex(d =>
      d.isCurrentMonth && d.value.getDate() === targetDay
    );

    if (targetIndex === -1) {
      // Find last day of current month
      targetIndex = newDates.findIndex(d => d.isCurrentMonth);
      if (targetIndex !== -1) {
        // Find the last day of current month
        for (let i = targetIndex; i < newDates.length; i++) {
          if (!newDates[i].isCurrentMonth) {
            targetIndex = i - 1;
            break;
          }
          if (i === newDates.length - 1) {
            targetIndex = i;
          }
        }
      }
    }

    if (targetIndex >= 0) {
      const targetDate = newDates[targetIndex];
      const targetValue = targetDate.value.getTime();

      this.setActiveDateFocus(targetValue);

      const target = this.querySelector(`[data-value="${targetValue}"]`);
      if (target instanceof HTMLElement) {
        this.applyFocusStyle(target);
        target.focus();
        this.announceDate(targetDate);
      }
    }
  }

  setActiveDateFocus(dateValue) {
    const allDates = this.querySelectorAll(".mx-calendar__date");
    allDates.forEach(d => d.setAttribute("tabindex", "-1"));

    const el = this.querySelector(`[data-value="${dateValue}"]`);
    if (el) el.setAttribute("tabindex", "0");
  }

  restoreDateFocus(dateValue) {
    requestAnimationFrame(() => {
      const target = this.querySelector(`[data-value="${dateValue}"]`);

      if (target instanceof HTMLElement) {
        this.setActiveDateFocus(dateValue);
        this.applyFocusStyle(target);
        target.focus();
      }
    });
  }

  /**
   * @param {HTMLDivElement} target
   * @param {boolean} keepOpen
   */
  selectDate(target, keepOpen = false) {
    const dateValue = Number(target.getAttribute("data-value"));
    const selectedDate = new Date(dateValue);

    const dateString = selectedDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    this.announce(`Selected ${dateString}`);

    this.value = selectedDate;
    this.render();

    // Format date as YYYY-MM-DD string using local date components to avoid timezone issues
    const isoDateString = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

    const selectEvent = new CustomEvent("DateChanged", {
      detail: isoDateString,
      bubbles: true,
    });
    // @ts-ignore
    selectEvent.keepOpenOnSelect = keepOpen;
    this.dispatchEvent(selectEvent);

    if (keepOpen) {
      this.restoreDateFocus(dateValue);
    }
  }
  renderDates() {
    /** @type CalendarDate[] */
    const dates = [];
    const today = new Date();
    const firstDay = new Date(this.year, this.month).getDay();
    const totalDaysInMonth = getDaysInMonth(this.month, this.year);
    const totalDaysInPrevMonth = getDaysInMonth(this.month - 1, this.year);
    // This is the top left corner
    const previousMonthStartDate = totalDaysInPrevMonth - (firstDay - 1);

    // Previous month's dates
    for (let i = previousMonthStartDate; i <= totalDaysInPrevMonth; i++) {
      const date = new Date(this.year, this.month - 1, i);

      dates.push({
        date: i,
        value: date,
        isCurrentMonth: false,
        isDisabled: today > date,
        isToday: false,
      });
    }

    // Current month's dates
    for (let i = 1; i <= totalDaysInMonth; i++) {
      const date = new Date(this.year, this.month, i);
      const isToday = isSameDay(today, date);
      const isDisabled = isBefore(date, today) && !isToday;

      dates.push({
        date: i,
        value: date,
        isCurrentMonth: true,
        isDisabled,
        isToday,
      });
    }

    // Next month's dates
    if (dates.length < calendarSize) {
      const offset = calendarSize - dates.length;
      for (let i = 1; i <= offset; i++) {
        const date = new Date(this.year, this.month + 1, i);
        dates.push({
          date: i,
          value: date,
          isCurrentMonth: false,
          isDisabled: false,
          isToday: false,
        });
      }
    }

    // Group dates into rows (7 days per week)
    const rows = [];
    for (let i = 0; i < dates.length; i += 7) {
      rows.push(dates.slice(i, i + 7));
    }

    return rows
      .map(
        (row, rowIndex) => {
          const rowCells = row
            .map((d, colIndex) => {
              const fullDateString = d.value.toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              });

              let ariaLabel = fullDateString;
              if (d.isToday) {
                ariaLabel += ", this is today";
              }
              if (d.isDisabled) {
                ariaLabel += ", not available";
              }
              if (!d.isCurrentMonth) {
                ariaLabel += ", different month";
              }

              const selectedDate = parseLocalDateValue(this.value);
              const isSelected = selectedDate ? isSameDay(d.value, selectedDate) : false;
              if (isSelected) {
                ariaLabel += ", currently selected";
              }

              return `<div 
                role="gridcell" 
                aria-colindex="${colIndex + 1}"
                data-disabled=${d.isDisabled}
                data-today=${d.isToday} 
                data-value=${d.value.getTime()} 
                data-current-month=${d.isCurrentMonth}
                data-active=${isSelected}
                class="mx-calendar__date"
                aria-label="${ariaLabel}"
                aria-current="${d.isToday ? 'date' : 'false'}"
                aria-selected="${isSelected}"
                tabindex="-1">
                ${d.date}
              </div>`;
            })
            .join("");

          return `<div role="row" aria-rowindex="${rowIndex + 1}">${rowCells}</div>`;
        }
      )
      .join("");
  }

  /**
   * Check if navigation should be disabled based on current date and boundaries
   */
  getNavigationState() {
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const calendarMonth = new Date(this.year, this.month, 1);
    
    // Check if we're at the minimum allowed month (current month)
    const isAtMinMonth = calendarMonth <= currentMonth;
    
    return {
      canGoPrevMonth: !isAtMinMonth,
      canGoPrevYear: !isAtMinMonth || this.year > today.getFullYear(),
      canGoNextMonth: true, // Always allow next month navigation
      canGoNextYear: true  // Always allow next year navigation
    };
  }

  render() {
    const currentString = new Date(this.year, this.month).toLocaleString(
      "en-CA",
      {
        month: "short",
        year: "numeric",
      }
    );
    const hasSelection = this.value != null && !(typeof this.value === 'string' && this.value === "");
    const navState = this.getNavigationState();

    this.innerHTML = `
    <div class="mx-calendar ${hasSelection ? "has-selection" : ""}" 
         role="dialog" 
         aria-modal="true"
         aria-labelledby="calendar-label"
         aria-describedby="calendar-announcement"
         aria-label="Date picker dialog">
      <!-- Label for the dialog (invisible but announced) -->
      <div id="calendar-label" class="sr-only">
        Choose a date. Press Page Up or Page Down to change month. Press Enter to select a date.
      </div>
      
      <nav class="mx-calendar__nav" 
           role="group" 
           aria-label="Calendar navigation">
        <button class="mx-calendar__btn" 
                data-value="-1" 
                data-type="year"
                aria-label="Previous year"
                tabindex="0"
                style="pointer-events: auto; user-select: auto;"
                ${!navState.canGoPrevYear ? 'disabled aria-disabled="true"' : ''}>
          <i class="mx-icon-double-left"></i>
        </button>
        <button class="mx-calendar__btn" 
                data-value="-1" 
                data-type="month"
                aria-label="Previous month"
                tabindex="0"
                style="pointer-events: auto; user-select: auto;"
                ${!navState.canGoPrevMonth ? 'disabled aria-disabled="true"' : ''}>
          <i class="mx-icon-left"></i>
        </button>
        <button 
                aria-live="polite" 
                aria-atomic="true"
                class="mx-calendar__current-month"
                aria-label="${currentString}">
          ${currentString}
        </button>
        <button class="mx-calendar__btn" 
                data-value="1" 
                data-type="month"
                aria-label="Next month"
                tabindex="0"
                style="pointer-events: auto; user-select: auto;">
          <i class="mx-icon-right"></i>
        </button>
        <button class="mx-calendar__btn" 
                data-value="1" 
                data-type="year"
                aria-label="Next year"
                tabindex="0"
                style="pointer-events: auto; user-select: auto;">
          <i class="mx-icon-double-right"></i>
        </button>
      </nav>
      <div class="mx-calendar__weekdays" 
           role="rowgroup">
           ${daysOfWeek
        .map((day, index) =>
          `<div role="columnheader" 
                      aria-label="${day.full}">
                 ${day.short}
               </div>`
        )
        .join("")}
      </div>
      <div class="mx-calendar__dates" 
           role="grid" 
           aria-label="Calendar dates"
           aria-labelledby="calendar-label"
           aria-multiselectable="false">
        ${this.renderDates()}
      </div>
      <!-- Screen reader announcements -->
      <div class="sr-only" 
           aria-live="polite" 
           aria-atomic="true"
           id="calendar-announcement">
      </div>
      
      <!-- Keyboard commands information -->
      <div class="sr-only" 
           aria-live="polite" 
           aria-atomic="true"
           id="calendar-help">
        Use arrow keys to navigate dates. Page Up or Page Down to change month. Enter to select a date. Escape to close.
      </div>
    </div>
    `;

    // Set initial focus after render
    setTimeout(() => this.setInitialFocus(), 0);
  }
}

customElements.define("mx-reservation-calendar", ReservationCalendar);
